// Shared confirm-review flow for Models list row + bulk bar.
import { useCallback, useRef, useState } from "react";
import { confirmManufacturingRecipesReview } from "@/lib/manufacturingClient";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";

export function isRecipeConfirmEligible(
  recipe: Pick<MfgRecipeSummary, "id" | "needs_review">,
  pendingCounts: Readonly<Record<string, number>>,
): boolean {
  if (!recipe.needs_review) return false;
  return (pendingCounts[recipe.id] ?? 0) === 0;
}

export function useManufacturingModelsConfirmReview(opts: {
  onDone: () => void;
  onError: (msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const onDoneRef = useRef(opts.onDone);
  const onErrorRef = useRef(opts.onError);
  onDoneRef.current = opts.onDone;
  onErrorRef.current = opts.onError;

  const confirmIds = useCallback(async (recipeIds: readonly string[]) => {
    const ids = [...new Set(recipeIds.filter(Boolean))];
    if (ids.length === 0) return;
    setBusy(true);
    const result = await confirmManufacturingRecipesReview({ recipe_ids: ids });
    setBusy(false);
    if (!result.ok) {
      onErrorRef.current(result.error || "Confirm failed");
      return;
    }
    const blocked = result.data.blocked.length;
    const confirmed = result.data.confirmed.length;
    if (blocked > 0 && confirmed === 0) {
      onErrorRef.current(
        blocked === 1
          ? "Cannot confirm while pending material lines remain."
          : `Cannot confirm ${blocked} models — pending material lines remain.`,
      );
      return;
    }
    if (blocked > 0) {
      onErrorRef.current(
        `Confirmed ${confirmed}; ${blocked} still blocked by pending materials.`,
      );
    }
    onDoneRef.current();
  }, []);

  return { busy, confirmIds };
}
